import { buildFairValueSeries } from './seriesMath';
import { ratioCeiling, PE_MAX } from '../config/metricsConfig';

/**
 * Computes the sample mean and standard deviation of a [x,y][] series over
 * an inclusive x window. Null/undefined bounds are treated as unbounded, so
 * this also works before a chart's extremes have settled. Fewer than 2
 * points in the window yields std: 0 rather than NaN.
 *
 * `positiveOnly` (default false) additionally excludes non-positive (<= 0)
 * values from the average/std calculation, and `cap` (default null, no cap)
 * excludes values above it. Ratio-mode input is already validity-filtered by
 * buildRatioSeries, so these checks are defensive there; in yield mode,
 * `positiveOnly` deliberately keeps loss-making periods from skewing the
 * valuation overlay while the plotted yield line itself remains continuous.
 */
export const computeWindowStats = (series, xMin, xMax, positiveOnly = false, cap = null) => {
  const lo = xMin == null ? -Infinity : xMin;
  const hi = xMax == null ? Infinity : xMax;
  const values = series
    .filter(([x, y]) => (
      y != null && x >= lo && x <= hi && (!positiveOnly || y > 0) && (cap == null || y <= cap)
    ))
    .map(([, y]) => y);

  const n = values.length;
  if (n === 0) return { avg: null, std: 0, n: 0 };
  const avg = values.reduce((a, b) => a + b, 0) / n;
  if (n === 1) return { avg, std: 0, n: 1 };
  const variance = values.reduce((acc, v) => acc + (v - avg) ** 2, 0) / (n - 1);
  return { avg, std: Math.sqrt(variance), n };
};

/**
 * First and last timestamp of a [x,y][] series, regardless of null y — used
 * so an overlay can span a metric's whole plotted domain even though its
 * value was computed from only a subset of it.
 */
export const seriesXDomain = (series) => (
  series.length ? [series[0][0], series[series.length - 1][0]] : null
);

/**
 * A flat reference line + its ±std band, both spanning the given domain
 * rather than just the window the stats were computed from.
 */
export const buildHorizontalOverlay = (avg, std, domain) => {
  if (avg == null || !domain) return { avgData: [], bandData: [] };
  const [from, to] = domain;
  return {
    avgData: [[from, avg], [to, avg]],
    bandData: [[from, avg - std, avg + std], [to, avg - std, avg + std]],
  };
};

/**
 * Converts a yield-mode avg/std (metric/value × 100, e.g. Earnings Yield)
 * into the equivalent P/E-style multiple(s), for reuse with
 * buildFairValueSeries, which only understands multiples. Reciprocation
 * flips ordering (a lower yield is a higher multiple: `avg - std` is the
 * *lower* yield bound but reciprocates to the *higher* multiple bound, and
 * vice versa) — the lower-yield side is also the side that can be at or
 * below zero (a volatile/loss-making window), which has no finite
 * reciprocal.
 *
 * A near-zero yield reciprocates into an implausibly large multiple that
 * blows out the main chart's y-axis scale — this happens on either side:
 * a lower-yield bound (`avg - std`) that's negative/zero has no finite
 * reciprocal at all, and one that's a *tiny positive* number (a volatile
 * window whose std nearly equals its mean — e.g. a symbol whose earnings
 * yield ranged from ~40% down to ~2% over the window) reciprocates to a
 * finite-but-absurd multiple (100 / 0.01% = 1e6). Both are the same
 * near-zero-denominator artifact, not a genuine valuation signal — the
 * exact case `PE_MAX` (see metricsConfig) exists to reject. Any bound that
 * comes out at or below zero, or above `PE_MAX`, is left unresolved (null);
 * buildFairValueOverlay falls back to the central `multiple` for it, so the
 * band simply doesn't extend on that side instead of exploding. The central
 * `multiple` itself is dropped entirely if even it exceeds `PE_MAX` (a whole
 * window averaging a near-zero yield), collapsing the overlay rather than
 * drawing a nonsensical fair value. This is a plain reciprocal, not a
 * statistically rigorous transform of the distribution — matching the level
 * of rigor buildRatioSeries already applies to ratio/yield.
 */
export const reciprocalMultipleRange = (avg, std) => {
  if (avg == null || avg <= 0) return { multiple: null, lowMultiple: null, highMultiple: null };
  // A finite reciprocal only within the plausible (0, PE_MAX] range; a
  // near-zero yield that reciprocates above PE_MAX is a near-zero-denominator
  // artifact, treated the same as a non-positive (unreciprocable) yield.
  const boundedReciprocal = (yieldPct) => {
    if (yieldPct == null || yieldPct <= 0) return null;
    const m = 100 / yieldPct;
    return m > PE_MAX ? null : m;
  };
  return {
    multiple: boundedReciprocal(avg),
    lowMultiple: boundedReciprocal(avg + std), // higher yield -> lower multiple; avg + std > 0 always
    highMultiple: boundedReciprocal(avg - std), // lower yield -> higher multiple; unresolved at/below zero or above PE_MAX
  };
};

/**
 * The main-chart fair-value overlay: an avg-multiple line plus a ±std band,
 * built by calling buildFairValueSeries over the metric's full, uncropped
 * series — so, just like the existing manual multiple line, it naturally
 * spans the metric's whole history/estimate range rather than being
 * clipped to whatever window the multiple was computed from. An unresolved
 * (null) lowMultiple/highMultiple — see reciprocalMultipleRange — falls back
 * to `multiple` itself, so that side of the band collapses onto the avg line
 * rather than rendering a nonsensical value.
 *
 * `price` (optional) aligns the output onto Price's own timestamps, same as
 * buildFairValueSeries' own `price` param and for the same reason — this
 * avg line is tooltip.shared with the dense daily Price series on the main
 * chart, and without alignment the sparse metric-cadence points fracture
 * that shared tooltip into separate/misaligned boxes (see the bug report
 * this fixed, and buildFairValueSeries' docstring for the full explanation).
 */
export const buildFairValueOverlay = ({
  metricSeries, shares, displayScale, metric, multiple, lowMultiple, highMultiple, price,
}) => {
  if (multiple == null) return { avgData: [], bandData: [] };
  const avgLine = buildFairValueSeries(metricSeries, shares, multiple, displayScale, metric, price);
  const lowLine = buildFairValueSeries(metricSeries, shares, lowMultiple ?? multiple, displayScale, metric, price);
  const highLine = buildFairValueSeries(metricSeries, shares, highMultiple ?? multiple, displayScale, metric, price);
  return {
    avgData: avgLine,
    bandData: avgLine.map((p, i) => [p.x, lowLine[i].y, highLine[i].y]),
  };
};

/**
 * Orchestrator for the ratio chart's overlay: stats over the selected
 * window, drawn across the ratio series' full domain. Excludes non-positive
 * and (optionally, via `cap` — see metricsConfig.js's `ratioCeiling`)
 * implausibly large values defensively; ratio-mode lines normally contain
 * gaps instead of those values, while yield-mode lines can retain negative
 * values that should not influence the positive valuation reference.
 */
export const computeRatioOverlay = (ratioSeries, xMin, xMax, cap = null) => {
  const { avg, std } = computeWindowStats(ratioSeries, xMin, xMax, true, cap);
  return buildHorizontalOverlay(avg, std, seriesXDomain(ratioSeries));
};

/**
 * The largest y-value actually present in `series` within an inclusive
 * [xMin, xMax] window (null/undefined bounds are unbounded, matching
 * computeWindowStats' convention). Returns null if the window contains no
 * non-null points. Unlike computeWindowStats, this doesn't exclude
 * non-positive or capped values -- it's used to detect whether a cap is
 * needed at all, not to compute a statistic that a spike should be excluded
 * from.
 */
export const visibleSeriesMax = (series, xMin, xMax) => {
  const lo = xMin == null ? -Infinity : xMin;
  const hi = xMax == null ? Infinity : xMax;
  let max = null;
  series.forEach(([x, y]) => {
    if (y != null && x >= lo && x <= hi && (max == null || y > max)) max = y;
  });
  return max;
};

/**
 * The ratio chart's y-axis `max`, if one is needed at all -- `undefined`
 * (no explicit cap, so Highcharts auto-scales the axis to whatever's
 * actually visible in [xMin, xMax], same as it would with no `max` option
 * at all) unless the widest per-metric ceiling among currently ticked
 * metrics (see metricsConfig.js's `ratioCeiling`) is actually exceeded by
 * real data within that same window -- e.g. a near-zero-denominator spike
 * currently in view, which would otherwise blow out the shared scale for
 * every other ticked metric.
 *
 * MUST be recomputed for the current [xMin, xMax] on every window change
 * (zoom/pan/range-selector), not computed once and left in place: a
 * Highcharts explicit axis `max` is a hard pin that Highcharts itself never
 * relaxes on its own as the visible window later changes (confirmed via
 * direct Highcharts instrumentation -- an axis with an explicit `max` set
 * for one window keeps that exact max after a later setExtremes zooms to a
 * narrower window, even though the new window's own real data tops out far
 * below it). An earlier version of this cap was computed once per
 * fetch/mode-change from the ticked metrics' ceilings ALONE (ignoring
 * xMin/xMax entirely) and then never revisited -- since the default view
 * (ratio mode, EPS ticked) always has a finite ceiling, that meant the
 * ratio chart's y-axis was effectively *always* pinned to a fixed ceiling
 * regardless of zoom, which is the root cause of the "ratio chart y-axis
 * doesn't rescale to the visible window" bug (see PROJECT_TRACKER.md's
 * Change Log for this fix).
 */
export const computeRatioAxisMax = (tickedMetrics, ratioSeriesByMetric, valueBasis, ratioMode, xMin, xMax) => {
  const ceiling = tickedMetrics
    .map((m) => ratioCeiling(m, valueBasis, ratioMode))
    .filter((c) => c != null)
    .reduce((widest, c) => Math.max(widest, c), -Infinity);
  if (!Number.isFinite(ceiling)) return undefined;

  const visibleMax = tickedMetrics.reduce((max, m) => {
    const seriesMax = visibleSeriesMax(ratioSeriesByMetric[m] || [], xMin, xMax);
    if (seriesMax == null) return max;
    return max == null ? seriesMax : Math.max(max, seriesMax);
  }, null);

  return (visibleMax != null && visibleMax > ceiling) ? ceiling : undefined;
};

/**
 * Orchestrator for the main chart's overlay: stats over the selected
 * window on the passed-in ratio series, converted to a multiple range,
 * then applied across the metric's full series. Returns the resolved
 * multiple too, so the caller can label the fair-value line's tooltip
 * (e.g. "× 15.3"). Excludes non-positive and implausibly large values from
 * the avg/std calculation, same as computeRatioOverlay above.
 *
 * This overlay is ALWAYS driven on the MULTIPLE basis, by design — the main
 * chart's fair value is the metric's own averaged P/E (EV/E, …), never a
 * reciprocated averaged yield, and that must stay independent of the lower
 * ratio chart's Ratio/Yield display toggle (averaging a P/E and reciprocating
 * an averaged yield are non-equivalent — coupling the two was a real bug; see
 * buildMainChartSeries' docstring). There is deliberately NO `ratioMode`
 * parameter here: making it multiple-only structurally forbids the yield path
 * on this chart rather than relying on every caller to remember to pass
 * 'ratio'. The yield→multiple conversion still lives, standalone and
 * unit-tested, in `reciprocalMultipleRange` for a future *explicit* yield-
 * based valuation feature to reuse — such a feature would get its own
 * orchestrator rather than resurrecting a mode branch here.
 */
export const computeFairValueOverlay = ({
  ratioSeries, metricSeries, shares, displayScale, metric, xMin, xMax, price, cap,
}) => {
  const { avg, std } = computeWindowStats(ratioSeries, xMin, xMax, true, cap);
  const multiple = avg;
  const lowMultiple = avg == null ? null : avg - std;
  const highMultiple = avg == null ? null : avg + std;
  return {
    ...buildFairValueOverlay({ metricSeries, shares, displayScale, metric, multiple, lowMultiple, highMultiple, price }),
    multiple,
  };
};

/**
 * Precomputes the expensive price-aligned skeleton (interpJoin, marker
 * overrides, basis conversion) once at multiple=1. The returned array has
 * one entry per output point: { x, rawValue, marker?, ratio?, estDetail? }
 * — everything that is invariant across window changes. On each drag tick
 * the caller just scales `rawValue * newMultiple` instead of re-running
 * buildFairValueSeries 3× and the O(n²) ratioAtFrom enrichment.
 */
export const buildFairValueSkeleton = ({ metricSeries, shares, displayScale, metric, price }) => {
  const line = buildFairValueSeries(metricSeries, shares, 1, displayScale, metric, price);
  return line.map((p) => ({ x: p.x, rawValue: p.rawValue, ...(p.marker ? { marker: p.marker } : {}) }));
};

/**
 * Fast-path overlay from a precomputed skeleton: produces avgData and bandData
 * by scaling the cached rawValues, avoiding interpJoin/Set/basis-conversion.
 * Each avgData point carries band bounds (low/high) inline so the caller can
 * skip a separate withBandBounds pass.
 */
export const overlayFromSkeleton = (skeleton, multiple, lowMultiple, highMultiple) => {
  if (multiple == null || !skeleton.length) return { avgData: [], bandData: [], multiple };
  const lo = lowMultiple ?? multiple;
  const hi = highMultiple ?? multiple;
  const avgData = new Array(skeleton.length);
  const bandData = new Array(skeleton.length);
  for (let i = 0; i < skeleton.length; i++) {
    const p = skeleton[i];
    const yLo = p.rawValue * lo;
    const yHi = p.rawValue * hi;
    avgData[i] = { x: p.x, y: p.rawValue * multiple, rawValue: p.rawValue, low: yLo, high: yHi, ratio: p.ratio, estDetail: p.estDetail, ...(p.marker ? { marker: p.marker } : {}) };
    bandData[i] = [p.x, yLo, yHi];
  }
  return { avgData, bandData, multiple };
};
